import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { adminAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { BrandLogo } from "../components/Brand";
import { toast } from "sonner";
import {
    BarChart3,
    Users,
    BookOpen,
    Trash2,
    Edit,
    Plus,
    ArrowLeft,
    Shield,
    ShieldOff,
    Sparkles,
    X,
    Save,
} from "lucide-react";

const SUBJECTS = [
    "Constitutional Law", "Contracts", "Torts", "Criminal Law and Procedure",
    "Evidence", "Civil Procedure", "Property", "Business Associations"
];

export default function AdminPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [users, setUsers] = useState([]);
    const [subjectFilter, setSubjectFilter] = useState("");
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [genSubject, setGenSubject] = useState(SUBJECTS[0]);
    const [genCount, setGenCount] = useState(5);

    const loadStats = useCallback(async () => {
        try {
            const res = await adminAPI.getStats();
            setStats(res.data);
        } catch { /* ignore */ }
    }, []);

    const loadQuestions = useCallback(async () => {
        try {
            const params = {};
            if (subjectFilter) params.subject = subjectFilter;
            const res = await adminAPI.getQuestions(params);
            setQuestions(res.data);
        } catch { /* ignore */ }
    }, [subjectFilter]);

    const loadUsers = useCallback(async () => {
        try {
            const res = await adminAPI.getUsers();
            setUsers(res.data);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        if (!user?.is_admin) { navigate("/dashboard"); return; }
        loadStats();
        loadQuestions();
        loadUsers();
    }, [user, navigate, loadStats, loadQuestions, loadUsers]);

    useEffect(() => { loadQuestions(); }, [subjectFilter, loadQuestions]);

    const handleDeleteQuestion = async (id) => {
        if (!window.confirm("Delete this question?")) return;
        try {
            await adminAPI.deleteQuestion(id);
            toast.success("Question deleted");
            loadQuestions();
            loadStats();
        } catch { toast.error("Failed to delete"); }
    };

    const handleSaveQuestion = async () => {
        try {
            await adminAPI.updateQuestion(editingQuestion.id, {
                subject: editingQuestion.subject,
                text: editingQuestion.text,
                options: editingQuestion.options,
                correct_option_id: editingQuestion.correct_option_id,
                explanation: editingQuestion.explanation,
                difficulty: editingQuestion.difficulty,
            });
            toast.success("Question updated");
            setEditingQuestion(null);
            loadQuestions();
        } catch { toast.error("Failed to update"); }
    };

    const handleToggleAdmin = async (userId) => {
        try {
            const res = await adminAPI.toggleAdmin(userId);
            toast.success(`Admin status: ${res.data.is_admin}`);
            loadUsers();
        } catch { toast.error("Failed to toggle admin"); }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const res = await adminAPI.generateQuestions(genSubject, genCount);
            toast.success(`Generated ${res.data.created} questions`);
            loadQuestions();
            loadStats();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to generate");
        } finally { setGenerating(false); }
    };

    const handleSeed = async () => {
        try {
            await adminAPI.seedQuestions();
            toast.success("Questions seeded");
            loadQuestions();
            loadStats();
        } catch { toast.error("Failed to seed"); }
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <BrandLogo />
                        <Badge variant="outline" className="text-amber-400 border-amber-400">Admin</Badge>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 py-8">
                <Tabs defaultValue="dashboard">
                    <TabsList className="mb-6">
                        <TabsTrigger value="dashboard"><BarChart3 className="w-4 h-4 mr-2" />Dashboard</TabsTrigger>
                        <TabsTrigger value="questions"><BookOpen className="w-4 h-4 mr-2" />Questions</TabsTrigger>
                        <TabsTrigger value="users"><Users className="w-4 h-4 mr-2" />Users</TabsTrigger>
                    </TabsList>

                    {/* DASHBOARD TAB */}
                    <TabsContent value="dashboard">
                        {stats && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                                <Card><CardHeader><CardTitle className="text-sm text-slate-400">Total Questions</CardTitle></CardHeader>
                                    <CardContent><p className="text-3xl font-bold text-white">{stats.total_questions}</p></CardContent></Card>
                                <Card><CardHeader><CardTitle className="text-sm text-slate-400">Total Users</CardTitle></CardHeader>
                                    <CardContent><p className="text-3xl font-bold text-white">{stats.total_users}</p></CardContent></Card>
                                <Card><CardHeader><CardTitle className="text-sm text-slate-400">Total Games</CardTitle></CardHeader>
                                    <CardContent><p className="text-3xl font-bold text-white">{stats.total_games}</p></CardContent></Card>
                            </div>
                        )}
                        {stats?.questions_by_subject && (
                            <Card>
                                <CardHeader><CardTitle>Questions by Subject</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {Object.entries(stats.questions_by_subject).map(([subject, count]) => (
                                            <div key={subject} className="flex justify-between items-center p-3 rounded-lg bg-slate-800/50">
                                                <span className="text-slate-300">{subject}</span>
                                                <Badge>{count}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* QUESTIONS TAB */}
                    <TabsContent value="questions">
                        <div className="flex flex-wrap gap-3 mb-6">
                            <select
                                value={subjectFilter}
                                onChange={(e) => setSubjectFilter(e.target.value)}
                                className="bg-slate-800 text-white border border-slate-700 rounded-md px-3 py-2 text-sm"
                            >
                                <option value="">All Subjects</option>
                                {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <div className="flex items-center gap-2 ml-auto">
                                <select
                                    value={genSubject}
                                    onChange={(e) => setGenSubject(e.target.value)}
                                    className="bg-slate-800 text-white border border-slate-700 rounded-md px-3 py-2 text-sm"
                                >
                                    {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <Input
                                    type="number" min={1} max={20} value={genCount}
                                    onChange={(e) => setGenCount(Number(e.target.value))}
                                    className="w-20 bg-slate-800 border-slate-700"
                                />
                                <Button onClick={handleGenerate} disabled={generating} size="sm">
                                    <Sparkles className="w-4 h-4 mr-1" />{generating ? "Generating..." : "AI Generate"}
                                </Button>
                                <Button onClick={handleSeed} variant="outline" size="sm">
                                    <Plus className="w-4 h-4 mr-1" />Seed
                                </Button>
                            </div>
                        </div>

                        <Card>
                            <CardContent className="p-0">
                                <div className="divide-y divide-slate-800">
                                    {questions.length === 0 && (
                                        <p className="p-6 text-center text-slate-500">No questions found</p>
                                    )}
                                    {questions.map((q) => (
                                        <div key={q.id} className="p-4 hover:bg-slate-800/30">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Badge variant="outline" className="text-xs">{q.subject}</Badge>
                                                        <Badge variant="secondary" className="text-xs">{q.difficulty}</Badge>
                                                    </div>
                                                    <p className="text-sm text-slate-300 line-clamp-2">{q.text}</p>
                                                </div>
                                                <div className="flex gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => setEditingQuestion({...q})}>
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteQuestion(q.id)}
                                                        className="text-red-400 hover:text-red-300">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* USERS TAB */}
                    <TabsContent value="users">
                        <Card>
                            <CardContent className="p-0">
                                <div className="divide-y divide-slate-800">
                                    {users.map((u) => (
                                        <div key={u.id} className="p-4 flex items-center justify-between">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-white font-medium">{u.username}</span>
                                                    {u.is_admin && <Badge className="bg-amber-500/20 text-amber-400">Admin</Badge>}
                                                </div>
                                                <p className="text-sm text-slate-400">{u.email}</p>
                                                <p className="text-xs text-slate-500">
                                                    Level {u.level || 1} | {u.games_played || 0} games | Score: {u.total_score || 0}
                                                </p>
                                            </div>
                                            <Button
                                                variant="outline" size="sm"
                                                onClick={() => handleToggleAdmin(u.id)}
                                                className={u.is_admin ? "text-red-400 border-red-400/50" : "text-emerald-400 border-emerald-400/50"}
                                            >
                                                {u.is_admin ? <><ShieldOff className="w-4 h-4 mr-1" />Remove Admin</> : <><Shield className="w-4 h-4 mr-1" />Make Admin</>}
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>

            {/* EDIT QUESTION MODAL */}
            {editingQuestion && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-white">Edit Question</h3>
                            <Button variant="ghost" size="sm" onClick={() => setEditingQuestion(null)}><X className="w-4 h-4" /></Button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm text-slate-400 mb-1 block">Subject</label>
                                    <select value={editingQuestion.subject}
                                        onChange={(e) => setEditingQuestion({...editingQuestion, subject: e.target.value})}
                                        className="w-full bg-slate-800 text-white border border-slate-700 rounded-md px-3 py-2 text-sm">
                                        {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm text-slate-400 mb-1 block">Difficulty</label>
                                    <select value={editingQuestion.difficulty}
                                        onChange={(e) => setEditingQuestion({...editingQuestion, difficulty: e.target.value})}
                                        className="w-full bg-slate-800 text-white border border-slate-700 rounded-md px-3 py-2 text-sm">
                                        <option value="easy">Easy</option>
                                        <option value="medium">Medium</option>
                                        <option value="hard">Hard</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-sm text-slate-400 mb-1 block">Question Text</label>
                                <textarea value={editingQuestion.text}
                                    onChange={(e) => setEditingQuestion({...editingQuestion, text: e.target.value})}
                                    rows={3} className="w-full bg-slate-800 text-white border border-slate-700 rounded-md px-3 py-2 text-sm" />
                            </div>

                            {editingQuestion.options?.map((opt, i) => (
                                <div key={opt.id} className="flex items-center gap-2">
                                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                        editingQuestion.correct_option_id === opt.id ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-300"
                                    }`}>{opt.id}</span>
                                    <Input value={opt.text}
                                        onChange={(e) => {
                                            const opts = [...editingQuestion.options];
                                            opts[i] = {...opts[i], text: e.target.value};
                                            setEditingQuestion({...editingQuestion, options: opts});
                                        }}
                                        className="flex-1 bg-slate-800 border-slate-700" />
                                    <Button variant={editingQuestion.correct_option_id === opt.id ? "default" : "ghost"} size="sm"
                                        onClick={() => setEditingQuestion({...editingQuestion, correct_option_id: opt.id})}>
                                        Correct
                                    </Button>
                                </div>
                            ))}

                            <div>
                                <label className="text-sm text-slate-400 mb-1 block">Explanation</label>
                                <textarea value={editingQuestion.explanation}
                                    onChange={(e) => setEditingQuestion({...editingQuestion, explanation: e.target.value})}
                                    rows={3} className="w-full bg-slate-800 text-white border border-slate-700 rounded-md px-3 py-2 text-sm" />
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button variant="ghost" onClick={() => setEditingQuestion(null)}>Cancel</Button>
                                <Button onClick={handleSaveQuestion}><Save className="w-4 h-4 mr-1" />Save</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
